// src/components/AiAssistant/AiAssistantWidget.tsx
//
// Widget chat de l'assistant IA conversationnel SAGeCom (v1).
//
// UX :
//   • Bouton flottant (FAB) en bas à droite — gradient bleu→indigo→violet
//   • Panel chat 96 (desktop) / fullscreen mobile, h-[600px] max-h-[80vh]
//   • Messages user à droite (bleu), assistant à gauche (gris/blanc)
//   • Textarea avec Enter pour envoyer, Shift+Enter pour saut de ligne,
//     Cmd/Ctrl+Enter aussi (pratique sur Mac)
//   • État vide : message d'accueil + 3-4 suggestions cliquables
//   • Loading assistant : 3 points pulsants
//   • Erreur Anthropic : encadré rouge sous le message
//   • Conversation active stockée dans localStorage pour persister entre F5
//   • Une conversation n'est créée qu'au PREMIER envoi (pas avant — pour
//     éviter de polluer la BD).
//
// Mai 2026.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BRAND_NAME } from '../../config/env';
import {
  Sparkles, X, Send, Plus, History as HistoryIcon, Bot, Minus,
  ChevronDown, MessageSquare, AlertTriangle, Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { markdownEnHtmlProp, CLASSES_MARKDOWN } from '../../utils/markdown';
import {
  aiAssistantService,
  AiConversation,
  AiMessage,
} from '../../services/aiAssistantService';
import { getContextPage } from '../../utils/aiContext';
import toast from '../../utils/toast';
import HistoriqueConversations from './HistoriqueConversations';

const STORAGE_KEY = 'sagecom_ai_conv_id';

const SUGGESTIONS = [
  'Comment générer un bulletin de paie ?',
  `Quels modules ${BRAND_NAME} me sont accessibles ?`,
  'Comment fonctionne le module Location ?',
  'Comment créer une demande de décaissement ?',
];

// Suggestions affichées en contexte ÉLECTRICITÉ (page facturation électrique,
// branchements, carte SIG…). Elles déclenchent les outils tool-use sur les
// vraies données → rendent la capacité « IA électricité » immédiatement visible.
const SUGGESTIONS_ELECTRICITE = [
  'Liste les factures travaux',
  'Factures électriques impayées ce mois',
  'Combien de branchements actifs ?',
  'Total des cautions de branchement',
];

const SUGGESTIONS_RH = [
  "Combien d'employés actifs dans mon entité ?",
  'Quelles demandes de congé sont en attente ?',
  'Résume la masse salariale du mois',
  'Postes ouverts au recrutement ?',
];

// Suggestions en contexte E-LEARNING : l'assistant sert de tuteur sur le
// contenu du cours ouvert, pas de guichet RH.
const SUGGESTIONS_ELEARNING = [
  'Explique-moi simplement cette séquence',
  'Résume les points à retenir pour le quiz',
  'Donne un exemple concret sur le terrain',
  'Pourquoi cette étape est-elle obligatoire ?',
];

const SUGGESTIONS_SELF_SERVICE = [
  'Quel est mon solde de congés ?',
  'Où en sont mes demandes en cours ?',
  'Comment soumettre une demande de formation ?',
  'Que faire pour une demande de disponibilité ?',
];

function getSuggestionsForModule(mod: string | null) {
  if (mod === 'electricite') return SUGGESTIONS_ELECTRICITE;
  if (mod === 'rh') return SUGGESTIONS_RH;
  if (mod === 'elearning') return SUGGESTIONS_ELEARNING;
  if (mod === 'self-service') return SUGGESTIONS_SELF_SERVICE;
  return SUGGESTIONS;
}

function getAccueilForModule(mod: string | null): string {
  if (mod === 'electricite') {
    return 'Mode électricité : je peux interroger vos vraies données — factures (consommation, travaux, matériel, caution), consommations, impayés, contrats et branchements.';
  }
  if (mod === 'rh') {
    return "Bonjour ! Je suis votre assistant Capital humain. Je peux interroger vos données RH en temps réel — effectifs, congés, masse salariale, recrutements...";
  }
  if (mod === 'elearning') {
    return "Bonjour ! Je suis votre tuteur de formation. Posez-moi vos questions sur le cours en cours — je reformule, je donne des exemples et je vous prépare au quiz.";
  }
  if (mod === 'self-service') {
    return "Bonjour ! Je suis votre assistant Self Service. Je peux vous aider avec vos demandes, votre solde de congés, vos bulletins de paie...";
  }
  return '';
}

// ─── Composant principal ──────────────────────────────────────────────────────

const AiAssistantWidget: React.FC = () => {
  const { hasPermission, isAuthenticated, user } = useAuth();
  const canSee = isAuthenticated && hasPermission('access_ai_assistant');

  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [conversation, setConversation] = useState<AiConversation | null>(null);
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fermer le menu si clic à l'extérieur
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-scroll en bas à chaque nouveau message
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  // Charger la conversation persistée à l'ouverture (et si non chargée)
  const loadInitialConversation = useCallback(async () => {
    if (!canSee) return;
    setLoading(true);
    try {
      const persistedId = localStorage.getItem(STORAGE_KEY);
      if (persistedId) {
        try {
          const conv = await aiAssistantService.getConversation(persistedId);
          if (conv && !conv.archivee) {
            setConversation(conv);
            setMessages(Array.isArray(conv.messages) ? conv.messages : []);
            return;
          }
        } catch {
          // Conversation introuvable / supprimée → on retombe sur la dernière
          localStorage.removeItem(STORAGE_KEY);
        }
      }
      // Sinon, dernière conversation non archivée
      const list = await aiAssistantService.listConversations({ limit: 1 });
      if (Array.isArray(list) && list.length > 0) {
        const conv = await aiAssistantService.getConversation(list[0].id);
        setConversation(conv);
        setMessages(Array.isArray(conv.messages) ? conv.messages : []);
        localStorage.setItem(STORAGE_KEY, conv.id);
      } else {
        setConversation(null);
        setMessages([]);
      }
    } catch (e: any) {
      // Pas de toast bruyant ici — c'est un chargement de fond
      console.warn('[AiAssistant] loadInitialConversation', e?.message);
    } finally {
      setLoading(false);
    }
  }, [canSee]);

  // Au montage (une fois authentifié + permission)
  useEffect(() => {
    if (canSee && open && !conversation && !loading) {
      loadInitialConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSee, open]);

  // Envoyer un message
  const handleSend = async () => {
    const contenu = input.trim();
    if (!contenu || sending) return;
    setSending(true);

    const ctx = getContextPage();

    // 1) Optimistic UI : on affiche le message user tout de suite
    const optimisticUser: AiMessage = {
      id: `tmp-${Date.now()}`,
      conversationId: conversation?.id ?? 'pending',
      role: 'user',
      contenu,
      contextePage: ctx.contextePage,
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimisticUser]);
    setInput('');

    try {
      // 2) Si pas de conversation active → en créer une
      let convId = conversation?.id;
      if (!convId) {
        const created = await aiAssistantService.createConversation({
          contextePage: ctx.contextePage,
          contexteModule: ctx.contexteModule,
          employeId: (user as any)?.employeId ?? null,
        });
        convId = created.id;
        setConversation(created);
        localStorage.setItem(STORAGE_KEY, created.id);
      }

      // 3) Envoi du message
      const res = await aiAssistantService.sendMessage(convId, {
        contenu,
        contextePage: ctx.contextePage,
        contexteModule: ctx.contexteModule,
        employeId: (user as any)?.employeId ?? null,
      });

      // 4) On remplace le message optimiste par le vrai user + on ajoute la réponse
      setMessages(prev => {
        const withoutTmp = prev.filter(m => m.id !== optimisticUser.id);
        return [...withoutTmp, res.messageUser, res.messageAssistant];
      });
      // Mettre à jour la conversation (nbMessages, titre éventuellement)
      if (res.conversation) {
        setConversation(res.conversation);
      }
    } catch (e: any) {
      // Retirer le message optimiste, garder le contenu dans l'input pour réessayer
      setMessages(prev => prev.filter(m => m.id !== optimisticUser.id));
      setInput(contenu);
      const msg = e?.response?.data?.message || "Échec de l'envoi du message";
      toast.error(typeof msg === 'string' ? msg : "Échec de l'envoi du message");
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl+Enter envoie aussi
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
      return;
    }
    // Enter envoie, Shift+Enter = saut de ligne
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = () => {
    setConversation(null);
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
    setMenuOpen(false);
    textareaRef.current?.focus();
  };

  const handleOpenConversation = async (id: string) => {
    setLoading(true);
    try {
      const conv = await aiAssistantService.getConversation(id);
      setConversation(conv);
      setMessages(Array.isArray(conv.messages) ? conv.messages : []);
      localStorage.setItem(STORAGE_KEY, conv.id);
    } catch {
      toast.error('Impossible de charger cette conversation');
    } finally {
      setLoading(false);
    }
  };

  const handleConversationDeleted = (id: string) => {
    if (conversation?.id === id) {
      setConversation(null);
      setMessages([]);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const handleSuggestion = (s: string) => {
    setInput(s);
    textareaRef.current?.focus();
  };

  if (!canSee) return null;

  return (
    <>
      {/* ── Bouton flottant (FAB) ── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 group"
          aria-label="Ouvrir l'assistant IA"
        >
          <div className="relative">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 shadow-xl flex items-center justify-center text-white transition-transform group-hover:scale-110">
              <Sparkles className="w-6 h-6" />
            </div>
            {/* Halo pulsant */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400 via-indigo-400 to-purple-500 animate-ping opacity-20 pointer-events-none" />
          </div>
        </button>
      )}

      {/* ── Panel chat ── */}
      {open && (
        <div
          className="fixed bottom-2 inset-x-2 sm:bottom-6 sm:right-6 sm:left-auto sm:inset-x-auto sm:w-96 h-[600px] max-h-[calc(100vh-4rem)] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-100 overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                  <Bot className="w-5 h-5" />
                </div>
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-400 border-2 border-white rounded-full" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold leading-tight">Assistant {BRAND_NAME}</p>
                <p className="text-[11px] text-white/80 leading-tight flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                  En ligne
                </p>
              </div>
            </div>

            {/* Menu */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(v => !v)}
                  className="p-1.5 rounded-lg hover:bg-white/15 transition"
                  aria-label="Menu"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-1.5 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50">
                    <button
                      onClick={() => { handleNewConversation(); }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4 text-gray-400" />
                      Nouvelle conversation
                    </button>
                    <button
                      onClick={() => { setMenuOpen(false); setHistoryOpen(true); }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <HistoryIcon className="w-4 h-4 text-gray-400" />
                      Historique
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/15 transition"
                aria-label="Réduire"
                title="Réduire"
              >
                <Minus className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/15 transition"
                aria-label="Fermer"
                title="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body — messages */}
          <div
            ref={bodyRef}
            className="flex-1 overflow-y-auto min-h-0 p-3 bg-gradient-to-b from-gray-50 to-white space-y-3"
          >
            {loading && messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mb-2" />
                <p className="text-xs">Chargement...</p>
              </div>
            ) : messages.length === 0 ? (
              /* État vide — accueil */
              <div className="flex flex-col items-center text-center py-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 flex items-center justify-center text-white mb-3 shadow-lg">
                  <Sparkles className="w-7 h-7" />
                </div>
                <h4 className="text-base font-bold text-gray-800 mb-1">
                  Bonjour, comment puis-je vous aider ?
                </h4>
                <p className="text-xs text-gray-500 mb-4 max-w-xs">
                  {(() => {
                    const accueil = getAccueilForModule(getContextPage().contexteModule);
                    return accueil
                      ? accueil
                      : <>Posez-moi vos questions sur {BRAND_NAME}, vos modules, vos procédures métier.</>;
                  })()}
                </p>
                <div className="w-full space-y-1.5">
                  {getSuggestionsForModule(getContextPage().contexteModule).map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSuggestion(s)}
                      className="w-full text-left text-xs text-gray-700 bg-white hover:bg-indigo-50 hover:text-indigo-700 border border-gray-200 hover:border-indigo-200 rounded-xl px-3 py-2 transition flex items-start gap-2"
                    >
                      <MessageSquare className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0 mt-0.5" />
                      <span>{s}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Liste des messages */
              messages.map((m) => <MessageBubble key={m.id} message={m} />)
            )}

            {/* Indicateur "thinking" */}
            {sending && (
              <div className="flex items-start gap-2 animate-fade-in">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 flex items-center justify-center text-white flex-shrink-0">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3 py-2.5 shadow-sm">
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer — input */}
          <div className="border-t bg-white px-3 py-2.5 flex-shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Écrivez votre message..."
                rows={1}
                disabled={sending}
                className="flex-1 resize-none text-sm px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400 max-h-[120px]"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || sending}
                className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white flex items-center justify-center shadow hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Envoyer"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">
              Entrée pour envoyer · Shift+Entrée pour saut de ligne
            </p>
          </div>
        </div>
      )}

      {/* ── Modal historique ── */}
      <HistoriqueConversations
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        currentConversationId={conversation?.id ?? null}
        onOpenConversation={handleOpenConversation}
        onNewConversation={handleNewConversation}
        onConversationDeleted={handleConversationDeleted}
      />
    </>
  );
};

// ─── Sous-composant : bulle de message ─────────────────────────────────────────

const MessageBubble: React.FC<{ message: AiMessage }> = ({ message }) => {
  const isUser = message.role === 'user';
  const time = new Date(message.createdAt).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (isUser) {
    return (
      <div className="flex justify-end animate-fade-in">
        <div className="max-w-[85%] flex flex-col items-end">
          <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-2xl rounded-tr-sm px-3 py-2 shadow-sm">
            <p className="text-sm whitespace-pre-wrap break-words">{message.contenu}</p>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5 px-1">{time}</p>
        </div>
      </div>
    );
  }

  // Assistant — la réponse est du MARKDOWN (titres, listes, tableaux GFM).
  // Rendue telle quelle dans un <p>, elle affichait ses astérisques et ses
  // barres verticales ; elle passe donc par marked, puis par la désinfection
  // maison avant d'être insérée (cf. utils/markdown.ts).
  return (
    <div className="flex items-start gap-2 animate-fade-in">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 flex items-center justify-center text-white flex-shrink-0 mt-0.5">
        <Bot className="w-4 h-4" />
      </div>
      <div className="max-w-[85%] flex flex-col items-start min-w-0">
        <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm min-w-0 max-w-full overflow-hidden">
          <div
            className={CLASSES_MARKDOWN}
            dangerouslySetInnerHTML={markdownEnHtmlProp(message.contenu)}
          />
        </div>
        {message.erreur && (
          <div className="mt-1.5 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5 flex items-start gap-1.5 max-w-full">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-red-700 leading-snug">
              Service IA indisponible — réessayez plus tard.
            </p>
          </div>
        )}
        <p className="text-[10px] text-gray-400 mt-0.5 px-1">{time}</p>
      </div>
    </div>
  );
};

export default AiAssistantWidget;
